import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, UserRound } from "lucide-react";
import { CLIENT_AREA_URL, openCallReason, scrollToId } from "@/lib/site";

const NAV_LEFT = [
  { label: "Il metodo", target: "percorso-yu-follow", testid: "nav-metodo" },
  { label: "Servizi", target: "servizi", testid: "nav-servizi" },
];

const NAV_RIGHT = [
  { label: "Domande", target: "domande-frequenti", testid: "nav-domande" },
];

const NAV_ALL = [...NAV_LEFT, ...NAV_RIGHT];

const SITE_INDEX = [
  { label: "Quanta concorrenza hai", target: "concorrenza", desktopOnly: true },
  { label: "Fai tu il conto", target: "il-conto" },
  { label: "Le strutture che seguo", target: "strutture" },
  { label: "Chi ti segue", target: "chi-sono" },
  { label: "I nostri servizi", target: "servizi" },
  { label: "I percorsi su misura", target: "percorso-su-misura" },
  { label: "Il metodo", target: "percorso-yu-follow" },
  { label: "Domande frequenti", target: "domande-frequenti" },
  { label: "Guide e articoli", target: "guide", href: "/guide" },
  { label: "Prenota la tua call", target: "call", highlight: true },
  { label: "Area Clienti", target: "client-area", external: true },
];

const linkCls = "nav-link font-mono text-xs uppercase tracking-widest text-[#A89F91]";

const EASE = [0.16, 1, 0.3, 1];

export default function Header() {
  const [active, setActive] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        setActive(NAV_ALL.map((n) => n.target).find((id) => visible.has(id)) || null);
      },
      { rootMargin: "-35% 0px -60% 0px" }
    );
    NAV_ALL.forEach(({ target }) => {
      const el = document.getElementById(target);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (menuOpen) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [menuOpen]);

  const navigate = useNavigate();
  const go = (target) => {
    setMenuOpen(false);
    setTimeout(() => scrollToId(target), 60);
  };

  const renderLink = (item) => (
    <button
      key={item.target}
      data-testid={item.testid}
      onClick={() => scrollToId(item.target)}
      className={`${linkCls} ${active === item.target ? "nav-link-active" : ""}`}
    >
      {item.label}
    </button>
  );

  return (
    <>
      <header
        data-testid="main-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-[#2A241F] bg-[#12100E]/85 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-10">
            <a
              href="#top"
              data-testid="header-logo"
              onClick={(e) => { e.preventDefault(); window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }); }}
            >
              <span className="flex items-baseline gap-1.5">
                <span className="font-heading text-xl font-black uppercase tracking-tight">YU</span>
                <span className="font-heading text-sm font-black uppercase tracking-tight text-[#E05A2C]">YieldUp</span>
              </span>
              <span className="mt-0.5 hidden font-mono text-xs uppercase tracking-[0.25em] text-[#A89F91] sm:block">Revenue management</span>
            </a>
            <nav className="hidden items-center gap-8 lg:flex">
              {[...NAV_LEFT, ...NAV_RIGHT].map(renderLink)}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <a
              data-testid="header-client-area-button"
              href={CLIENT_AREA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-glow flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-full bg-[#E05A2C] px-4 py-3 text-xs font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] sm:px-6 sm:text-sm"
            >
              <UserRound className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">Area Clienti</span>
            </a>
            <button
              data-testid="header-book-call-button"
              onClick={() => openCallReason("header")}
              className="btn-glow inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full bg-[#E05A2C] px-4 py-3 text-xs font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] sm:px-6 sm:text-sm"
            >
              <span className="sm:hidden">Prenota call</span>
              <span className="hidden sm:inline">Prenota una chiamata</span>
            </button>
            <button
              data-testid="hamburger-button"
              onClick={() => setMenuOpen(true)}
              aria-label="Apri indice delle sezioni"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#383029] text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C]"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="site-index"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-[80] flex flex-col bg-[#12100E]"
          >
            <div className="flex items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
              <span className="flex items-baseline gap-1.5">
                <span className="font-heading text-xl font-black uppercase tracking-tight">YU</span>
                <span className="font-heading text-sm font-black uppercase tracking-tight text-[#E05A2C]">YieldUp</span>
              </span>
              <button
                data-testid="site-index-close"
                onClick={() => setMenuOpen(false)}
                aria-label="Chiudi indice"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#383029] text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="px-8 pt-4 section-kicker lg:px-14">Indice — vai dritto dove ti serve</p>

            <nav className="mt-4 flex-1 overflow-y-auto px-8 pb-4 lg:px-14">
              <div className="grid gap-x-14 lg:grid-cols-2">
                {SITE_INDEX.map((item, i) => (
                  <motion.button
                    key={item.target}
                    data-testid={`index-link-${item.target}`}
                    onClick={() => {
                      if (item.external) { setMenuOpen(false); window.open(CLIENT_AREA_URL, "_blank", "noopener,noreferrer"); }
                      else if (item.href) { setMenuOpen(false); navigate(item.href); }
                      else if (item.target === "call") { setMenuOpen(false); openCallReason("indice"); }
                      else go(item.target);
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 + i * 0.035, duration: 0.45, ease: EASE }}
                    className={`group items-baseline gap-4 border-b border-[#2A241F] py-3.5 text-left sm:py-4 ${item.desktopOnly ? "hidden sm:flex" : "flex"}`}
                  >
                    <span className={`font-heading text-xl font-black uppercase tracking-tight transition-colors duration-300 sm:text-2xl lg:text-3xl ${item.highlight ? "text-[#E05A2C]" : "text-[#FAF7F2] group-hover:text-[#E05A2C]"}`}>
                      {item.label}
                    </span>
                  </motion.button>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
